import { useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import Label from 'modules/ui/atoms/Typography/Label'
import Row from 'modules/ui/atoms/Layout/Row'
import Column from 'modules/ui/atoms/Layout/Column'
import { InputProps } from 'modules/ui/atoms/Input'
import { SelectInput } from '../../atoms/SelectInput'
import Caption from 'modules/ui/atoms/Typography/Caption'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { DEFAULT_THEME_COLORS } from 'css/theme'

export type BirthdateInputProps = InputProps

interface Month {
  name: string
  days: number
}

interface BirthDateErrors {
  monthError: string
  dayError: string
  yearError: string
}

const BirthdateInput = ({ onChange, value, required, error, dataId = '' }: BirthdateInputProps) => {
  const { t } = useTranslation()
  const theme = DEFAULT_THEME_COLORS

  const isLeapYear = (year: number): boolean => {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  }

  const createDaysArray = (numberOfDays: number) => {
    return Array.from({ length: numberOfDays }, (v, k) => (k + 1).toString())
  }

  const [month, setMonth] = useState<string>('')
  const [day, setDay] = useState<string>('')
  const [year, setYear] = useState<string>('')
  const [days, setDays] = useState<string[]>(createDaysArray(31))

  const [dateErrors, setDateErrors] = useState<BirthDateErrors>({
    monthError: '',
    dayError: '',
    yearError: '',
  })

  const currentYear = new Date().getFullYear()

  const years = Array.from(new Array(101), (val, index) => (currentYear - index).toString())

  const daysPerMonths: Month[] = [
    { name: 'January', days: 31 },
    { name: 'February', days: 28 },
    { name: 'March', days: 31 },
    { name: 'April', days: 30 },
    { name: 'May', days: 31 },
    { name: 'June', days: 30 },
    { name: 'July', days: 31 },
    { name: 'August', days: 31 },
    { name: 'September', days: 30 },
    { name: 'October', days: 31 },
    { name: 'November', days: 30 },
    { name: 'December', days: 31 },
  ]

  const getDaysInMonth = (month: string, year: string): number => {
    const monthData = daysPerMonths.find((m) => m.name === month)
    if (!monthData) return 0

    if (month === 'February' && isLeapYear(parseInt(year))) {
      return 29
    }

    return monthData.days
  }

  useEffect(() => {
    if (
      month &&
      month !== t('BirthdateInput.selectInput.placeholders.month', 'Month') &&
      year &&
      year !== t('BirthdateInput.selectInput.placeholders.year', 'Year')
    ) {
      const daysInMonth = getDaysInMonth(month, year)
      setDays(Array.from({ length: daysInMonth }, (v, k) => (k + 1).toString()))
    }
  }, [month, year])

  // Split the date into month, day, and year
  const isValidDate = (year: string, month: string, day: string) => {
    const date = new Date(`${year}-${month}-${day}`)
    const check1 = date instanceof Date && !isNaN(date.getTime())

    const check2 = year !== '' && month !== '' && day !== '' && year.length === 4

    return check1 && check2
  }

  useEffect(() => {
    const [y, m, d] = String(value).split('-')

    if (!isValidDate(y, m, d)) return

    setMonth(m)
    setDay(d)
    setYear(y)
  }, [value])

  useEffect(() => {
    if (day === '' || month === '' || year === '') {
      return
    }

    const date = new Date(`${year}-${month}-${day}`)
    onChange(date)
  }, [year, month, day])

  const handleOnChangeMonth = (change: any) => {
    setMonth(change)
  }

  const handleOnChangeDay = (change: any) => {
    setDay(change)
  }

  const handleOnChangeYear = (change: any) => {
    setYear(change)
  }

  useEffect(() => {
    if (required) {
      const errors = {
        monthError:
          month === ''
            ? t('BirthdateInput.errors.monthRequired', 'Please provide birth month.')
            : '',
        dayError:
          day === '' ? t('BirthdateInput.errors.dayRequired', 'Please provide birth day.') : '',
        yearError:
          year === '' ? t('BirthdateInput.errors.yearRequired', 'Please provide birth year.') : '',
      }

      setDateErrors({
        ...dateErrors,
        ...errors,
      })
    }
  }, [month, day, year])

  return (
    <div style={{ marginTop: '12px' }}>
      <Label errored={!!error} size="sm">
        {t('BirthdateInput.label.birthDate', 'Birth Date')}
      </Label>
      <Row>
        <Column style={{ flex: 1.9 }}>
          <SelectInput
            error={error && dateErrors.monthError}
            id="select-month"
            onChange={handleOnChangeMonth}
            value={month}
            required={required}
            placeholder={t('BirthdateInput.selectInput.placeholders.month', 'Month')}
            options={[
              {
                value: 'January',
                label: t('BirthdateInput.selectInput.options.january', 'January'),
              },
              {
                value: 'February',
                label: t('BirthdateInput.selectInput.options.february', 'February'),
              },
              { value: 'March', label: t('BirthdateInput.selectInput.options.march', 'March') },
              { value: 'April', label: t('BirthdateInput.selectInput.options.april', 'April') },
              { value: 'May', label: t('BirthdateInput.selectInput.options.may', 'May') },
              { value: 'June', label: t('BirthdateInput.selectInput.options.june', 'June') },
              { value: 'July', label: t('BirthdateInput.selectInput.options.july', 'July') },
              { value: 'August', label: t('BirthdateInput.selectInput.options.august', 'August') },
              {
                value: 'September',
                label: t('BirthdateInput.selectInput.options.september', 'September'),
              },
              {
                value: 'October',
                label: t('BirthdateInput.selectInput.options.october', 'October'),
              },
              {
                value: 'November',
                label: t('BirthdateInput.selectInput.options.november', 'November'),
              },
              {
                value: 'December',
                label: t('BirthdateInput.selectInput.options.december', 'December'),
              },
            ]}
            dataId={dataId ? `${dataId}-month` : ''}
          />
        </Column>
        <Column style={{ flex: 1.2 }}>
          <SelectInput
            error={error && dateErrors.dayError}
            id="select-day"
            onChange={handleOnChangeDay}
            value={day}
            required={required}
            placeholder={t('BirthdateInput.selectInput.placeholders.day', 'Day')}
            options={days}
            dataId={dataId ? `${dataId}-day` : ''}
          />
        </Column>
        <Column style={{ flex: 1.7 }}>
          <SelectInput
            error={error && dateErrors.yearError}
            id="select-year"
            onChange={handleOnChangeYear}
            value={year}
            required={required}
            placeholder={t('BirthdateInput.selectInput.placeholders.year', 'Year')}
            options={years}
            dataId={dataId ? `${dataId}-year` : ''}
          />
        </Column>
      </Row>

      {error && !dateErrors.dayError && !dateErrors.monthError && !dateErrors.yearError && (
        <Caption errored={!!error} aria-live="assertive">
          <span aria-hidden="true">
            <UiIcon
              aria-hidden="true"
              name="error"
              color={theme['--theme-color--status-error']}
              w={16}
              h={16}
            />
          </span>
          {error}
        </Caption>
      )}
    </div>
  )
}

export default BirthdateInput
